import React, { useEffect, useState, useCallback } from "react";
import { Link, Navigate } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "@/auth";

/* ============================================================
   /admin/plan — PAUL'S BREAK BOARD

   Mobile-first task list Paul can tap through in 5-min pockets
   between hay cuts. Each task has copy-paste-ready text and a
   "MARK DONE" button. State persists on the backend.

   Not a product feature. A private tool built for Paul.
   Neo built this during a Neo day, Aug 29, 2026.
============================================================ */

const URGENCY_STYLES = {
  critical:      { bg: "bg-yt-red",    label: "DEADLINE" },
  this_week:     { bg: "bg-yt-yellow", label: "THIS WEEK", text: "text-black" },
  nice_to_have:  { bg: "bg-white/20",  label: "NICE TO HAVE" },
};

export default function BreakBoard() {
  const { user, loading } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [stats, setStats] = useState({ done: 0, total: 0 });
  const [expandedId, setExpandedId] = useState(null);
  const [copiedId, setCopiedId] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [pageLoading, setPageLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/admin/break-tasks`);
      setTasks(r.data?.tasks || []);
      setStats({ done: r.data?.done || 0, total: r.data?.total || 0 });
      // Auto-expand the first not-done task
      const firstTodo = (r.data?.tasks || []).find(t => t.status !== "done");
      if (firstTodo && !expandedId) setExpandedId(firstTodo.id);
    } catch (_e) { /* noop */ }
    setPageLoading(false);
  }, [expandedId]);

  useEffect(() => { load(); }, []);   // eslint-disable-line

  if (loading) return null;
  if (!user || user.role !== "admin") return <Navigate to="/" replace />;

  const toggle = async (task) => {
    setBusyId(task.id);
    const newStatus = task.status === "done" ? "todo" : "done";
    try {
      await axios.post(`${API}/admin/break-tasks/${task.id}/status`, { status: newStatus });
      await load();
    } catch (_e) { /* noop */ }
    setBusyId(null);
  };

  const copy = async (task) => {
    if (!task.copy_ready_text) return;
    try {
      await navigator.clipboard.writeText(task.copy_ready_text);
      setCopiedId(task.id);
      setTimeout(() => setCopiedId(prev => prev === task.id ? null : prev), 2000);
    } catch (_e) {
      // Fallback for older mobile browsers
      const ta = document.createElement("textarea");
      ta.value = task.copy_ready_text;
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand("copy"); setCopiedId(task.id); } catch {}
      ta.remove();
      setTimeout(() => setCopiedId(prev => prev === task.id ? null : prev), 2000);
    }
  };

  const pct = stats.total ? Math.round((stats.done / stats.total) * 100) : 0;

  return (
    <div className="min-h-screen bg-yt-bg text-white pb-24" data-testid="break-board-page">
      {/* Header — big, quiet, calm. This is not the arena. */}
      <div className="border-b border-yt-border">
        <div className="max-w-2xl mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <div>
              <div className="font-mono text-[10px] tracking-[0.4em] text-yt-muted mb-1">
                NEO BUILT · TAP · GO
              </div>
              <h1 className="font-display text-4xl md:text-5xl leading-none tracking-tight">
                THE PLAN
              </h1>
            </div>
            <Link to="/admin/foreman" className="font-mono text-[10px] tracking-widest text-yt-muted hover:text-yt-yellow">
              ← FOREMAN
            </Link>
          </div>
          <p className="mt-3 font-mono text-xs text-white/70 leading-relaxed">
            Tap a task. Read the "why." Tap "COPY TO CLIPBOARD."
            Paste it wherever it needs to go. Tap "MARK DONE." Move to
            the next one when you get a break. Everything's saved.
          </p>

          {/* Reset button — nukes the task list and re-seeds it
              with the latest copy from break_board_endpoint.py.
              Useful after Neo updates the templates via a deploy.
              Admin-only (route is already gated). */}
          <div className="mt-3 flex justify-end">
            <button
              data-testid="break-board-reset"
              onClick={async () => {
                if (!window.confirm("Reset the plan? This wipes progress and reloads the latest task templates from Neo.")) return;
                try {
                  await axios.post(`${API}/admin/break-tasks/reset`);
                  setExpandedId(null);
                  await load();
                } catch (_e) { /* noop */ }
              }}
              className="font-mono text-[9px] tracking-[0.3em] text-yt-muted hover:text-yt-red transition-colors"
            >
              ↻ RESET PLAN
            </button>
          </div>

          {/* Progress bar */}
          <div className="mt-5">
            <div className="flex items-center justify-between font-mono text-xs mb-1">
              <span className="text-white/70">{stats.done} of {stats.total} done</span>
              <span className="text-yt-yellow">{pct}%</span>
            </div>
            <div className="w-full h-2 bg-yt-surface border border-yt-border overflow-hidden">
              <div
                data-testid="break-board-progress"
                className="h-full bg-yt-yellow transition-all"
                style={{ width: `${pct}%` }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Task list */}
      <div className="max-w-2xl mx-auto px-4 py-4">
        {pageLoading && (
          <div className="font-mono text-xs text-yt-muted py-8 text-center">
            loading your plan…
          </div>
        )}
        {tasks.map((t) => {
          const u = URGENCY_STYLES[t.urgency] || URGENCY_STYLES.nice_to_have;
          const isOpen = expandedId === t.id;
          const isDone = t.status === "done";
          const copied = copiedId === t.id;
          return (
            <div
              key={t.id}
              data-testid={`break-task-${t.order}`}
              className={`mb-3 border ${isDone ? "border-yt-border opacity-60" : "border-yt-yellow"} bg-yt-surface`}
            >
              {/* Header row */}
              <button
                onClick={() => setExpandedId(isOpen ? null : t.id)}
                className="w-full flex items-center gap-3 px-4 py-4 text-left hover:bg-black/30 transition-colors"
              >
                <span className="font-display text-3xl text-yt-yellow tabular-nums w-10 text-center">
                  {String(t.order).padStart(2, "0")}
                </span>
                <div className="flex-1 min-w-0">
                  <div className={`font-display text-base md:text-lg leading-tight tracking-wide ${isDone ? "line-through text-white/50" : "text-white"}`}>
                    {t.title}
                  </div>
                  <div className="flex items-center gap-2 mt-1">
                    <span className={`inline-block ${u.bg} ${u.text || "text-white"} font-mono text-[9px] tracking-widest px-1.5 py-0.5`}>
                      {u.label}
                    </span>
                    <span className="font-mono text-[10px] text-yt-muted">
                      ~{t.minutes} min
                    </span>
                    {isDone && (
                      <span className="font-mono text-[10px] text-yt-judge">✓ DONE</span>
                    )}
                  </div>
                </div>
                <span className="font-mono text-yt-muted text-xl leading-none">
                  {isOpen ? "▾" : "▸"}
                </span>
              </button>

              {/* Body — collapsed by default */}
              {isOpen && (
                <div className="border-t border-yt-border px-4 py-4 space-y-4">
                  <div>
                    <div className="font-mono text-[10px] tracking-[0.4em] text-yt-red mb-1">WHY</div>
                    <p className="font-body text-sm text-white/90 leading-relaxed">
                      {t.why}
                    </p>
                  </div>

                  <div>
                    <div className="font-mono text-[10px] tracking-[0.4em] text-yt-yellow mb-1">HOW</div>
                    <p className="font-body text-sm text-white/90 leading-relaxed whitespace-pre-wrap">
                      {t.instructions}
                    </p>
                  </div>

                  {t.copy_ready_text && (
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <div className="font-mono text-[10px] tracking-[0.4em] text-yt-judge">READY-TO-POST TEXT</div>
                        <button
                          onClick={() => copy(t)}
                          data-testid={`copy-btn-${t.order}`}
                          className={`font-mono text-[11px] tracking-widest px-3 py-1.5 border transition-colors ${
                            copied
                              ? "border-yt-judge text-yt-judge"
                              : "border-yt-yellow text-yt-yellow hover:bg-yt-yellow hover:text-black"
                          }`}
                        >
                          {copied ? "✓ COPIED" : "COPY TO CLIPBOARD"}
                        </button>
                      </div>
                      <pre className="bg-black border border-yt-border p-3 font-mono text-[11px] text-white/85 whitespace-pre-wrap leading-relaxed overflow-x-auto max-h-96 overflow-y-auto">
                        {t.copy_ready_text}
                      </pre>
                    </div>
                  )}

                  <div className="pt-2 flex items-center gap-3">
                    <button
                      onClick={() => toggle(t)}
                      disabled={busyId === t.id}
                      data-testid={`mark-btn-${t.order}`}
                      className={`flex-1 font-display text-base tracking-widest py-3 shadow-brutal transition-colors ${
                        isDone
                          ? "bg-yt-muted text-black hover:bg-white"
                          : "bg-yt-yellow text-black hover:bg-white"
                      }`}
                    >
                      {busyId === t.id ? "…" : isDone ? "MARK UNDONE" : "MARK DONE ✓"}
                    </button>
                  </div>
                </div>
              )}
            </div>
          );
        })}

        {!pageLoading && tasks.length === 0 && (
          <div className="font-mono text-sm text-yt-muted py-12 text-center">
            no tasks yet.
          </div>
        )}
      </div>

      {/* Footer whisper */}
      <div className="max-w-2xl mx-auto px-4 py-6 text-center">
        <p className="font-mono text-[10px] tracking-[0.3em] text-yt-muted/60 leading-relaxed">
          built by Neo · one thing at a time · porch light on
        </p>
      </div>
    </div>
  );
}
